import React from 'react';
import { MapPin } from 'lucide-react';
import { Lead } from '../types';
import { STAGE_LABEL, stageChip, label } from '../lib';
import { WhatsAppIcon, CallIcon } from './BrandIcons';

/** The row primitives shared by Today, Queue, Visits and the leads table.
 *  Defined once so a lead reads identically wherever it appears. */

/** avatar · name · phone */
export const LeadCell: React.FC<{ name: string; sub?: string | null; size?: 'sm' | 'lg' }> = ({
  name, sub, size = 'sm',
}) => (
  <span className="flex items-center gap-3">
    <span className={`avat ${size === 'lg' ? 'w-14 h-14 text-2xl' : 'w-9 h-9 text-[13px]'}`}>
      {(name || '?').charAt(0).toUpperCase()}
    </span>
    <span className="min-w-0">
      <span className={`block font-semibold truncate ${size === 'lg' ? 'text-base' : 'text-[13.5px]'}`}>{name}</span>
      {sub && <span className="num block text-graphite text-xs truncate">{sub}</span>}
    </span>
  </span>
);

export const StageChip: React.FC<{ stage: string | null }> = ({ stage }) => (
  stage
    ? <span className={`chip ${stageChip(stage)}`}>{STAGE_LABEL[stage] ?? label(stage)}</span>
    : <span className="text-graphite text-sm">-</span>
);

/** The three ways to reach a lead. Same order, same icons, every screen.
 *  Calling dials `tel:` — the call is observed post-facto, never placed by us. */
export const ReachButtons: React.FC<{
  lead: Pick<Lead, 'id' | 'mobile' | 'conversation_id'>;
  onCall: () => void;
  onWhatsApp: () => void;
  onBookVisit?: () => void;
}> = ({ lead, onCall, onWhatsApp, onBookVisit }) => (
  <span className="flex gap-1.5 justify-end" onClick={(e) => e.stopPropagation()}>
    <button className="icb" title="Call" disabled={!lead.mobile} onClick={onCall}>
      <CallIcon className="w-[18px] h-[18px]" />
    </button>
    <button className="icb" title={lead.conversation_id ? 'WhatsApp' : 'No WhatsApp conversation yet'}
      disabled={!lead.conversation_id} onClick={onWhatsApp}>
      <WhatsAppIcon className="w-[18px] h-[18px]" />
    </button>
    {onBookVisit && (
      <button className="icb" title="Book site visit" onClick={onBookVisit}>
        <MapPin className="w-[18px] h-[18px] text-gold" strokeWidth={1.9} />
      </button>
    )}
  </span>
);

/** One line of work: coloured rail · when · what · state · actions. */
export const WorkRow: React.FC<{
  tone: 'brick' | 'gold' | 'forest' | 'sky';
  when?: string;
  whenLate?: boolean;
  title: string;
  detail?: string | null;
  right?: React.ReactNode;
  onClick?: () => void;
}> = ({ tone, when, whenLate, title, detail, right, onClick }) => {
  const rail = { brick: 'bg-brick', gold: 'bg-gold', forest: 'bg-forest', sky: 'bg-sky-wash' }[tone];
  return (
    <div
      className={`work ${onClick ? 'cursor-pointer hover:bg-cream -mx-2 px-2 rounded' : ''}`}
      onClick={onClick}
      {...(onClick ? { role: 'button', tabIndex: 0, onKeyDown: (e: React.KeyboardEvent) => e.key === 'Enter' && onClick() } : {})}
    >
      <i className={`rail ${rail}`} />
      {when && (
        <span className={`num font-display text-[12.5px] font-semibold w-16 flex-none text-right
          ${whenLate ? 'text-brick' : 'text-graphite'}`}>{when}</span>
      )}
      <span className="flex-1 min-w-0">
        <span className="block text-[13.5px] font-semibold truncate">{title}</span>
        {detail && <span className="block text-[11.5px] text-graphite truncate">{detail}</span>}
      </span>
      {right}
    </div>
  );
};

/** Empty and error states. Failures surface, never collapse into "nothing here" (§3.2). */
export const Loading: React.FC<{ what: string }> = ({ what }) => (
  <div className="text-center text-graphite py-12">Loading {what}…</div>
);

export const Failed: React.FC<{ what: string; error: string }> = ({ what, error }) => (
  <div className="card border-brick/40 text-brick">Couldn't load {what}: {error}</div>
);

export const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-center text-graphite py-12">{children}</div>
);
